import React, { useState } from 'react';
import ABCampaignResults from './ABCampaignResults';
import {
    Dialog,
    DialogContent,
    DialogClose,
} from 'components/ui/dialog';
import {
    X, Info, Calendar, Users, Target, BarChart3, TrendingUp, TrendingDown,
    Mail, Send, Eye, MousePointer, DollarSign, Clock, Tag,
    Bot, Zap, Settings, Edit, Copy, Play, Pause,
} from 'lucide-react';
import { tone, toneForStatus } from "components/ui/tone";

const CampaignDetailsModal = ({
    isOpen,
    onClose,
    campaign,
    aiAgents = [],
    abTests = [],
    onEdit,
    onDuplicate,
    onPause,
    onResume,
    onViewABTests,
    onConfigureAI,
}) => {
    const [activeTab, setActiveTab] = useState('overview');

    if (!campaign) return null;

    /* ── Related data ─────────────────────────────── */
    const aiAgent = campaign.aiGenerated && campaign.aiAgentId
        ? aiAgents.find(a => a.id === campaign.aiAgentId)
        : null;

    /* ── Formatters ───────────────────────────────── */
    const fmtNum = (n) => {
        if (!n) return '0';
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
        if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
        return n.toLocaleString();
    };

    const fmtCurrency = (amt) => {
        if (!amt) return '$0';
        return new Intl.NumberFormat('en-US', {
            style: 'currency', currency: 'USD',
            minimumFractionDigits: 0, maximumFractionDigits: 0,
        }).format(amt);
    };

    const fmtPct = (v) => (!v ? '0.0%' : `${v.toFixed(1)}%`);

    const fmtDate = (d) => {
        if (!d) return 'Not set';
        try {
            const o = new Date(d);
            if (isNaN(o.getTime())) return 'Invalid Date';
            return o.toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        } catch { return 'Invalid Date'; }
    };

    const fmtShortDate = (d) => {
        if (!d) return 'Not set';
        try {
            const o = new Date(d);
            if (isNaN(o.getTime())) return 'Not set';
            return o.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch { return 'Not set'; }
    };

    const getStatusColor = (status) => tone(toneForStatus(status)).chip;

    const fmtStatus = (status) => {
        if (!status) return 'Unknown';
        if (status.toLowerCase() === 'pc') return 'Partially Completed';
        return status.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    };

    const isLiveStatus = (s) => ['active', 'progress'].includes((s ?? '').toLowerCase());

    const getTrend = (metrics) => {
        if (!metrics) return { icon: BarChart3, color: 'text-content-muted dark:text-gray-400', label: 'No Data' };
        const { openRate, clickRate } = metrics;
        if (openRate >= 25 && clickRate >= 5) return { icon: TrendingUp, color: 'text-emerald-600 dark:text-emerald-400', label: 'Excellent' };
        if (openRate >= 20 && clickRate >= 3) return { icon: TrendingUp, color: 'text-blue-600 dark:text-blue-400', label: 'Good' };
        if (openRate >= 15 && clickRate >= 2) return { icon: BarChart3, color: 'text-amber-600 dark:text-amber-400', label: 'Average' };
        return { icon: TrendingDown, color: 'text-red-500 dark:text-red-400', label: 'Needs Improvement' };
    };

    const trend = getTrend(campaign.metrics);
    const TrendIcon = trend.icon;

    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'performance', label: 'Performance' },
        { id: 'abtests', label: 'A/B Tests' },
        { id: 'automation', label: 'Automation' },
    ];

    const InfoRow = ({ icon: Icon, iconBg, iconColor, label, value }) => (
        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-700/50 rounded-xl border border-line-subtle dark:border-dark-600">
            <div className={`w-8 h-8 flex-shrink-0 ${iconBg} rounded-lg flex items-center justify-center`}>
                <Icon className={`w-4 h-4 ${iconColor}`} />
            </div>
            <div className="min-w-0">
                <div className="text-[12px] font-medium text-content-muted dark:text-gray-400">{label}</div>
                <div className="text-ui font-semibold text-content dark:text-white truncate">{value}</div>
            </div>
        </div>
    );

    const StatCard = ({ icon: Icon, iconColor, label, value, sub, accentBar }) => (
        <div className={`relative overflow-hidden rounded-xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 p-4 shadow-sm`}>
            <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${accentBar}`} />
            <div className="flex items-center gap-2 mb-2">
                <div className={`w-7 h-7 rounded-md flex items-center justify-center ${iconColor.replace('text-', 'bg-').replace('600', '50').replace('500', '50')} dark:${iconColor.replace('text-', 'bg-').replace('600', '900/20').replace('500', '900/20')}`}>
                    <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                </div>
                <span className="text-[13px] font-medium text-content-secondary dark:text-gray-300">{label}</span>
            </div>
            <div className="text-[22px] font-bold text-content dark:text-white leading-none">{value}</div>
            {sub && <div className="text-[12px] font-medium text-content-muted dark:text-gray-400 mt-1">{sub}</div>}
        </div>
    );

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent aria-labelledby="campaign-details-title"
                showClose={false}
                padding={false}
                className="w-[96vw] sm:w-[min(720px,94vw)] lg:w-[min(860px,94vw)]"
            >
                {/* ── Header ── */}
                <div className="relative px-6 pt-6 pb-0 flex-shrink-0">
                    {/* Accent bar */}
                    <div className="absolute top-0 inset-x-0 h-[2px] rounded-t-2xl bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />

                    <div className="flex items-start justify-between gap-4 pb-4 border-b border-line dark:border-dark-700">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2} />
                            </div>
                            <div className="min-w-0">
                                <h2 id="campaign-details-title" className="text-title font-bold text-content dark:text-white leading-tight truncate max-w-[380px]">
                                    {campaign.name}
                                </h2>
                                <p className="text-[13px] font-medium text-content-muted dark:text-gray-400 mt-0.5">
                                    Campaign Details &amp; Analytics
                                </p>
                            </div>
                        </div>

                        {/* Status badge + Close */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <div className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-semibold border ${getStatusColor(campaign.c_status || campaign.status)}`}>
                                <span className="relative flex h-1.5 w-1.5">
                                    {isLiveStatus(campaign.c_status || campaign.status) && (
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
                                    )}
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current" />
                                </span>
                                {fmtStatus(campaign.c_status || campaign.status)}
                            </div>

                            <DialogClose asChild>
                                <button
                                    onClick={onClose}
                                    className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg
                                        text-content-subtle hover:text-content-secondary dark:text-gray-500 dark:hover:text-gray-300
                                        hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors duration-150"
                                    aria-label="Close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </DialogClose>
                        </div>
                    </div>

                    {/* ── Tabs ── */}
                    <nav className="flex gap-1 -mb-px overflow-x-auto no-scrollbar">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-3 px-3 sm:px-4 border-b-2 text-ui font-semibold whitespace-nowrap transition-colors duration-150
                                    ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong dark:hover:border-dark-600'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* ── Scrollable body ── */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" style={{ scrollbarWidth: 'thin' }}>

                    {/* ════ OVERVIEW ════ */}
                    {activeTab === 'overview' && (
                        <div className="space-y-4">

                            {/* Status card */}
                            <div className="relative overflow-hidden rounded-xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 p-5 shadow-sm">
                                <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-blue-500 to-indigo-500" />

                                {/* Action row */}
                                <div className="flex flex-wrap items-center gap-2 mb-4">
                                    {/* Mobile status badge */}
                                    <div className={`sm:hidden inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-semibold border ${getStatusColor(campaign.c_status || campaign.status)}`}>
                                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                        {fmtStatus(campaign.c_status || campaign.status)}
                                    </div>

                                    {/* AI agent badge */}
                                    {campaign.aiGenerated && aiAgent && (
                                        <div className="flex items-center gap-2 px-2.5 py-1 rounded-lg text-[12px] font-semibold
                                            bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300
                                            border border-purple-100 dark:border-purple-800/40">
                                            <Bot className="w-3.5 h-3.5" />
                                            <span>{aiAgent.name}</span>
                                        </div>
                                    )}

                                    {/* Action buttons */}
                                    <div className="flex items-center gap-2 ml-auto">
                                        <button
                                            onClick={() => onEdit?.(campaign)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold
                                                border border-line dark:border-dark-600 rounded-lg
                                                text-gray-700 dark:text-gray-300
                                                hover:bg-gray-50 dark:hover:bg-dark-700
                                                hover:border-line-strong dark:hover:border-dark-500
                                                transition-all duration-150"
                                        >
                                            <Edit className="w-3.5 h-3.5" />
                                            <span className="hidden sm:inline">Edit</span>
                                        </button>
                                        <button
                                            onClick={() => onDuplicate?.(campaign)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold
                                                border border-line dark:border-dark-600 rounded-lg
                                                text-gray-700 dark:text-gray-300
                                                hover:bg-gray-50 dark:hover:bg-dark-700
                                                hover:border-line-strong dark:hover:border-dark-500
                                                transition-all duration-150"
                                        >
                                            <Copy className="w-3.5 h-3.5" />
                                            <span className="hidden sm:inline">Duplicate</span>
                                        </button>

                                        {(campaign.status === 'active' || campaign.c_status === 'active') ? (
                                            <button
                                                onClick={() => onPause?.(campaign.id)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold
                                                    rounded-lg text-white
                                                    bg-gradient-to-r from-orange-500 to-red-500
                                                    hover:from-orange-600 hover:to-red-600
                                                    transition-all duration-150 shadow-sm"
                                            >
                                                <Pause className="w-3.5 h-3.5" />
                                                <span className="hidden sm:inline">Pause</span>
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => onResume?.(campaign.id)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold
                                                    rounded-lg text-white
                                                    bg-gradient-to-r from-emerald-500 to-teal-500
                                                    hover:from-emerald-600 hover:to-teal-600
                                                    transition-all duration-150 shadow-sm"
                                            >
                                                <Play className="w-3.5 h-3.5" />
                                                <span className="hidden sm:inline">Resume</span>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {campaign.description && (
                                    <p className="text-ui font-medium text-content-secondary dark:text-gray-300 mb-4 leading-relaxed">
                                        {campaign.description}
                                    </p>
                                )}

                                {/* Info grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    <InfoRow icon={Calendar}
                                        iconBg="bg-blue-50 dark:bg-blue-900/20"
                                        iconColor="text-blue-600 dark:text-blue-400"
                                        label="Created"
                                        value={fmtShortDate(campaign.createdAt)}
                                    />
                                    <InfoRow icon={Users}
                                        iconBg="bg-emerald-50 dark:bg-emerald-900/20"
                                        iconColor="text-emerald-600 dark:text-emerald-400"
                                        label="Recipients"
                                        value={fmtNum(campaign.estimatedReach)}
                                    />
                                    <InfoRow icon={Target}
                                        iconBg="bg-violet-50 dark:bg-violet-900/20"
                                        iconColor="text-violet-600 dark:text-violet-400"
                                        label="Segment"
                                        value={campaign.segmentName || 'All Subscribers'}
                                    />
                                    <InfoRow icon={Mail}
                                        iconBg="bg-amber-50 dark:bg-amber-900/20"
                                        iconColor="text-amber-600 dark:text-amber-400"
                                        label="Template"
                                        value={campaign.templateName || 'Custom Template'}
                                    />
                                </div>
                            </div>

                            {/* Schedule */}
                            {(campaign.scheduledAt || campaign.sentAt) && (
                                <div className="relative overflow-hidden rounded-xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 p-5 shadow-sm">
                                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-indigo-500 to-violet-500" />
                                    <h3 className="text-[13px] font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide mb-3">
                                        Schedule
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {campaign.scheduledAt && (
                                            <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/40">
                                                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                                                    <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div>
                                                    <div className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">Scheduled For</div>
                                                    <div className="text-[13px] font-semibold text-blue-900 dark:text-blue-200">{fmtDate(campaign.scheduledAt)}</div>
                                                </div>
                                            </div>
                                        )}
                                        {campaign.sentAt && (
                                            <div className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-100 dark:border-emerald-800/40">
                                                <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                                                    <Send className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                                </div>
                                                <div>
                                                    <div className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Sent At</div>
                                                    <div className="text-[13px] font-semibold text-emerald-900 dark:text-emerald-200">{fmtDate(campaign.sentAt)}</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Tags */}
                            {campaign.tags?.length > 0 && (
                                <div className="relative overflow-hidden rounded-xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 p-5 shadow-sm">
                                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-amber-400 to-orange-500" />
                                    <h3 className="text-[13px] font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide mb-3">Tags</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {campaign.tags.map(tag => (
                                            <span key={tag}
                                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold
                                                    bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300
                                                    border border-line dark:border-dark-600">
                                                <Tag className="w-3 h-3" />
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ════ PERFORMANCE ════ */}
                    {activeTab === 'performance' && (
                        <div className="space-y-4">
                            {/* Trend header */}
                            <div className="flex items-center gap-2 px-1">
                                <TrendIcon className={`w-4 h-4 ${trend.color}`} />
                                <span className={`text-[13px] font-bold ${trend.color}`}>{trend.label}</span>
                            </div>

                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <StatCard
                                    icon={Eye} iconColor="text-blue-600 dark:text-blue-400"
                                    accentBar="from-blue-500 to-indigo-500"
                                    label="Open Rate"
                                    value={fmtPct(campaign.metrics?.openRate)}
                                    sub={`${fmtNum(campaign.metrics?.opens)} opens`}
                                />
                                <StatCard
                                    icon={MousePointer} iconColor="text-emerald-600 dark:text-emerald-400"
                                    accentBar="from-emerald-400 to-teal-500"
                                    label="Click Rate"
                                    value={fmtPct(campaign.metrics?.clickRate)}
                                    sub={`${fmtNum(campaign.metrics?.clicks)} clicks`}
                                />
                                <StatCard
                                    icon={Target} iconColor="text-violet-600 dark:text-violet-400"
                                    accentBar="from-violet-500 to-purple-500"
                                    label="Conversion"
                                    value={fmtPct(campaign.metrics?.conversionRate)}
                                    sub={`${fmtNum(campaign.metrics?.conversions)} conversions`}
                                />
                                <StatCard
                                    icon={DollarSign} iconColor="text-amber-600 dark:text-amber-400"
                                    accentBar="from-amber-400 to-orange-500"
                                    label="Revenue"
                                    value={fmtCurrency(campaign.metrics?.revenue)}
                                    sub={`${fmtPct(campaign.metrics?.roi)} ROI`}
                                />
                            </div>
                        </div>
                    )}

                    {/* ════ A/B TESTS ════ */}
                    {activeTab === 'abtests' && isOpen && (
                        <ABCampaignResults key={campaign.id} campaignId={campaign.id} />
                    )}

                    {/* ════ AUTOMATION ════ */}
                    {activeTab === 'automation' && (
                        <div className="space-y-4">
                            {/* AI Agent */}
                            {campaign.aiGenerated && aiAgent && (
                                <div className="relative overflow-hidden rounded-xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 p-5 shadow-sm">
                                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-violet-500 to-purple-500" />
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-[13px] font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide">AI Agent</h3>
                                        <button
                                            onClick={() => onConfigureAI?.(campaign.id, campaign.aiAgentId)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold
                                                border border-line dark:border-dark-600 rounded-lg
                                                text-gray-700 dark:text-gray-300
                                                hover:bg-gray-50 dark:hover:bg-dark-700
                                                transition-all duration-150"
                                        >
                                            <Settings className="w-3.5 h-3.5" />
                                            Configure
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-4 p-4 bg-violet-50 dark:bg-violet-900/20 rounded-xl border border-violet-100 dark:border-violet-800/40">
                                        <div className="w-10 h-10 bg-violet-100 dark:bg-violet-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <Bot className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-[14px] font-bold text-violet-900 dark:text-violet-200">{aiAgent.name}</h4>
                                            <p className="text-[12px] font-medium text-violet-700 dark:text-violet-300">{aiAgent.specialization} Specialist</p>
                                            <div className="flex items-center gap-3 mt-1.5 text-[12px] font-semibold text-violet-600 dark:text-violet-400">
                                                <span>Score: {aiAgent.performance?.score || 85}/100</span>
                                                <span>Confidence: {aiAgent.performance?.confidenceLevel || 92}%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Automation triggers */}
                            {campaign.automationTriggers?.length > 0 && (
                                <div className="relative overflow-hidden rounded-xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 p-5 shadow-sm">
                                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-blue-500 to-indigo-500" />
                                    <h3 className="text-[13px] font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide mb-3">Automation Triggers</h3>
                                    <div className="space-y-2">
                                        {campaign.automationTriggers.map((trigger, i) => (
                                            <div key={i}
                                                className="flex items-center gap-3 p-3 rounded-xl
                                                    border border-line-subtle dark:border-dark-600
                                                    bg-gray-50 dark:bg-dark-700/40">
                                                <div className="w-7 h-7 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                                    <Zap className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <span className="flex-1 text-[13px] font-medium text-gray-800 dark:text-gray-200">{trigger.description}</span>
                                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold
                                                    ${trigger.isActive
                                                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                                        : 'bg-gray-100 dark:bg-dark-600 text-content-muted dark:text-gray-400'
                                                    }`}>
                                                    {trigger.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!campaign.aiGenerated && !campaign.automationTriggers?.length && (
                                <div className="flex flex-col items-center justify-center py-10 text-center">
                                    <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center mb-3">
                                        <Bot className="w-6 h-6 text-content-subtle dark:text-gray-500" />
                                    </div>
                                    <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-200 mb-1">No automation configured</p>
                                    <p className="text-[12px] font-medium text-content-muted dark:text-gray-400">Enable AI features to automate campaign optimisation.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="px-6 pb-5 pt-3 border-t border-line dark:border-dark-700 flex-shrink-0">
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-[12px] font-medium text-content-subtle dark:text-gray-500">
                            ID: <span className="font-mono text-content-secondary dark:text-gray-300">{campaign.id}</span>
                        </p>
                        <DialogClose asChild>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-[13px] font-semibold
                                    bg-surface dark:bg-dark-700
                                    border border-line dark:border-dark-600
                                    text-gray-700 dark:text-gray-300
                                    rounded-xl hover:bg-gray-50 dark:hover:bg-dark-600
                                    hover:border-line-strong dark:hover:border-dark-500
                                    transition-all duration-150
                                    focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                            >
                                Close
                            </button>
                        </DialogClose>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CampaignDetailsModal;